import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import {
    activateExperiment,
    getExperiment,
    getPendingExperiment,
    setPendingExperiment,
    clearPendingExperiment,
    useCanUseExperiments,
    type ExperimentName,
} from "@/hooks/use-experiment";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useEffect, useState, type FC } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

/**
 * Captures experiment name from URL and stores it in localStorage.
 *
 * WHY MODULE-LEVEL EXECUTION:
 * This must run at module load time (before React renders) because:
 * 1. User visits /experiments/ralph
 * 2. AuthenticatedUserLayout checks auth → redirects to /login
 * 3. After login → may redirect to /onboarding
 * 4. After onboarding → redirects to / (losing the original URL)
 *
 * By capturing at module load, we store the intent before any redirects.
 * The PendingExperimentActivation component then processes it after auth.
 *
 * ALTERNATIVES CONSIDERED:
 * - Modifying AuthenticatedUserLayout: More invasive, affects all routes
 * - URL params through redirects: Complex, requires changes to login/onboarding
 * - This approach: Self-contained, only affects experiment routes
 */
function captureExperimentFromUrl() {
    if (typeof window === "undefined") return;

    const regex = /^\/experiments\/([^/]+)/;
    const match = regex.exec(window.location.pathname);
    if (match?.[1]) {
        const experimentName = match[1];
        const experiment = getExperiment(experimentName);
        if (experiment) {
            setPendingExperiment(experimentName);
        }
    }
}

captureExperimentFromUrl();

/**
 * Shows the toast notification for an activated experiment.
 */
function showExperimentActivatedToast(experimentName: string, experimentDisplayName: string) {
    if (experimentName === "ralph") {
        toast({
            title: (
                <span className="flex items-center gap-2">
                    <IconRalphWiggum size={24} />
                    <span>Ralph Mode enabled!</span>
                </span>
            ),
            description: "You can manage this in Settings → Experiments",
        });
    } else {
        toast({
            title: `${experimentDisplayName} enabled!`,
            description: "You can manage this in Settings → Experiments",
        });
    }
}

export const ExperimentsActivationPage: FC = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const { canUse, isEnterprise, loading: loadingOrg } = useCanUseExperiments();
    const [hasActivated, setHasActivated] = useState(false);
    const segmentTrack = useSegmentTrack();

    const experiment = name ? getExperiment(name) : null;

    useEffect(() => {
        // Wait for org data to load
        if (loadingOrg) return;

        // Unknown experiment - show toast and redirect
        if (!experiment && name) {
            toast({
                title: "Experiment not found",
                description: `The experiment "${name}" doesn't exist or is no longer available.`,
            });
            navigate("/", { replace: true });
            return;
        }

        // Enterprise users can't use experiments - show toast and redirect
        if (isEnterprise && experiment) {
            toast({
                title: "Not available on your plan",
                description: "Experiments are only available for Ona Cloud users.",
            });
            navigate("/", { replace: true });
            return;
        }

        // Activate if:
        // 1. We have a valid experiment
        // 2. User can use experiments (not enterprise)
        // 3. We haven't already activated
        if (experiment && canUse && !hasActivated && name) {
            activateExperiment(name as ExperimentName);
            setHasActivated(true);

            segmentTrack("experiment_enabled", {
                experiment_name: name,
                experiment_display_name: experiment.name,
                source: "activation_link",
            });

            showExperimentActivatedToast(name, experiment.name);
            navigate("/", { replace: true });
        }
    }, [experiment, canUse, isEnterprise, hasActivated, loadingOrg, name, navigate, segmentTrack]);

    // If we can use experiments and have activated, redirect happens in useEffect
    // This is a fallback in case the redirect hasn't happened yet
    if (hasActivated) {
        return <Navigate to="/" replace />;
    }

    // Loading state while activating
    return <LoadingState />;
};

/**
 * Component to process any pending experiment activation after login/onboarding.
 * Render this in the main app after authentication is confirmed.
 */
export const PendingExperimentActivation: FC<{ canUse: boolean; isEnterprise: boolean }> = ({
    canUse,
    isEnterprise,
}) => {
    const segmentTrack = useSegmentTrack();

    useEffect(() => {
        const pending = getPendingExperiment();
        if (!pending) return;

        const experiment = getExperiment(pending);
        if (!experiment) {
            clearPendingExperiment();
            return;
        }

        // If user can use experiments, activate it
        if (canUse && !isEnterprise) {
            activateExperiment(pending as ExperimentName);

            segmentTrack("experiment_enabled", {
                experiment_name: pending,
                experiment_display_name: experiment.name,
                source: "activation_link_after_auth",
            });

            showExperimentActivatedToast(pending, experiment.name);
        }

        clearPendingExperiment();
    }, [canUse, isEnterprise, segmentTrack]);

    return null;
};
